import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Script } from '@components/features/Script';
import { useUserQuery } from '@queries/user';
import { ENV } from '@utils/env';

interface PlainContextType {
  isReady: boolean;
  isOpen: boolean;
  show(): void;
}

const PlainContext = createContext<PlainContextType>({
  isReady: false,
  isOpen: false,
  show: () => {},
});

export function PlainProvider({ children }: PropsWithChildren) {
  const { data } = useUserQuery();
  const customerDetails = useMemo(() => {
    if (!data) return null;
    return {
      fullName: data.name,
      shortName: data.name?.split(' ')[0],
      emailHash: data.emailHash,
      email: data.email,
    };
  }, [data]);

  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const canLoad = !!ENV.plain.appId && !!customerDetails;

  // Show the Plain chat window
  const show = useCallback(() => {
    if (!isReady) return;
    window.Plain?.open();
  }, [isReady]);

  // Add event listeners for open and close
  useEffect(() => {
    if (!isReady) return;
    const Plain = window.Plain;
    if (!Plain) return;
    const offOpen = Plain.onOpen(() => setIsOpen(true));
    const offClose = Plain.onClose(() => setIsOpen(false));
    return () => {
      offOpen();
      offClose();
    };
  }, [isReady]);

  useEffect(() => {
    if (!isReady) return;
    if (!customerDetails) return;

    window.Plain?.setCustomerDetails(customerDetails);
  }, [isReady, customerDetails]);

  // Reset the state if the script can no longer be loaded
  useEffect(() => {
    if (!canLoad) {
      setIsReady(false);
    }
  }, [canLoad]);

  const onLoad = useCallback(async () => {
    if (!canLoad || !ENV.plain.appId) return;
    await window.Plain?.init({
      appId: ENV.plain.appId,
      customerDetails,
      hideLauncher: true,
    });
    setIsReady(true);
  }, [customerDetails]);

  const context = useMemo(
    () => ({ isReady, isOpen, show }),
    [isReady, isOpen, show]
  );

  const src = useMemo(() => {
    let src = 'https://chat.cdn-plain.com/index.js';
    if (ENV.pageProtection.scriptUrl) {
      const url = new URL(ENV.pageProtection.scriptUrl);
      const id = url.searchParams.get('id');
      src = `${url.origin}/${id}/${src}`;
    }
    return src;
  }, []);

  return (
    <PlainContext.Provider value={context}>
      {canLoad && (
        <Script src={src} onLoad={onLoad} async={false} data-plain-script />
      )}

      {children}
    </PlainContext.Provider>
  );
}

export function usePlain() {
  return useContext(PlainContext);
}
